// Viruses and spawners (architecture §9.3, art-assets §5, visuals §7).
// - classic (default, agar's look): 50 spikes = 100 vertices alternating every 3.6° from a tip at
//   +x, no rotation. Two fills like a cell's band: the outer polygon (tips r + 10, valleys r + 5)
//   in #2DE52D, then the inner one (tips r, valleys r − 5) in #33FF33. Jelly offsets (idle noise
//   ±1.5 u, wall and body contacts) move both polygons. No "nearly full" pulse;
// - sketch: `clamp(round(0.18·r), 12, 60)` spikes 8 % of r deep, scribbled (seeded jitter that
//   boils with the outline), an ink outline and a dashed inner ring;
// - sketch "nearly full" (≥ virusFeedsToShoot − 2 feeds, worked out from the radius with the
//   WELCOME constants; never in Experimental or Battle Royale, where viruses do not grow): wobbles
//   ±2 % at 6 Hz, or uses `world/virus-fed.png` if present. With Reduce motion it shows a lighter
//   fill instead of moving;
// - spawners (render/spawners.ts): the virus style in the spawner colour, rotating 0.2 rad/s;
// - user art (`world/virus.png`, `world/spawner.png`) replaces the procedural body: side 2.3 × r.

import {
  classicOutlineColor,
  isVirusNearlyFull,
  mixRgb,
  MODE_CODE,
  type Rgb,
  SIM_DEFAULTS,
  virusFeedsFromRadius,
  type WelcomeMessage,
} from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import type { EntityRecord } from '../game/world-store.js';
import { circleInRect } from './cull.js';
import { type Frame } from './frame.js';
import { noise3 } from './noise.js';
import { classicStrokePx, sketchStrokePx, traceCircle, unitRing } from './outline.js';
import { css, cssAlpha } from './theme.js';

const TAU = Math.PI * 2;

/** The classic (agar) virus geometry (visuals §7). */
export const CLASSIC_VIRUS = Object.freeze({
  spikes: 50,
  /** Inner polygon: tips at r, valleys at r − valleyU; outer: + bandU on both. */
  valleyU: 5,
  bandU: 10,
  /** Idle jelly noise amplitude (u). */
  noiseU: 1.5,
});

/** Vertex count of the classic virus polygon (tips and valleys). */
export const CLASSIC_VIRUS_VERTICES = CLASSIC_VIRUS.spikes * 2;

/**
 * The classic virus polygon's base radius at vertex `i` (even = tip, odd = valley): inner
 * (`outer` false) tips r, valleys r − 5; outer tips r + 10, valleys r + 5.
 */
export function classicVirusRadius(r: number, i: number, outer: boolean): number {
  const base = i % 2 === 0 ? r : r - CLASSIC_VIRUS.valleyU;
  return outer ? base + CLASSIC_VIRUS.bandU : base;
}

/** Adds the classic virus polygon (100 vertices from +x) with optional jelly offsets. */
export function traceClassicVirus(
  ctx: Frame['ctx'],
  x: number,
  y: number,
  r: number,
  outer: boolean,
  off: Float64Array | null,
): void {
  const n = CLASSIC_VIRUS_VERTICES;
  const { cos, sin } = unitRing(n);
  for (let i = 0; i < n; i++) {
    let rho = classicVirusRadius(r, i, outer) + (off ? (off[i] ?? 0) : 0);
    if (rho < 0) rho = 0;
    const px = x + rho * (cos[i] ?? 1);
    const py = y + rho * (sin[i] ?? 0);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/** A classic virus body (two fills) at the drawn position/radius; null `off` = no jelly. */
export function drawClassicVirus(
  f: Frame,
  x: number,
  y: number,
  r: number,
  fill: Rgb,
  off: Float64Array | null,
): void {
  const { ctx } = f;
  ctx.beginPath();
  traceClassicVirus(ctx, x, y, r, true, off);
  ctx.fillStyle = css(classicOutlineColor(fill));
  ctx.fill();
  ctx.beginPath();
  traceClassicVirus(ctx, x, y, r, false, off);
  ctx.fillStyle = css(fill);
  ctx.fill();
}

export const VIRUS_STYLE = Object.freeze({
  spikesPerRadius: 0.18,
  minSpikes: 12,
  maxSpikes: 60,
  /** Spike depth as a share of r: tips at r·(1 + tip), valleys at r·(1 − valley). */
  tip: 0.06,
  valley: 0.02,
  /** Image side = k × r for virus and spawner art (art-assets §5). */
  artK: 2.3,
  wobbleAmplitude: 0.02,
  wobbleHz: 6,
  spawnerRadPerSec: 0.2,
  /** Below this on-screen radius (CSS px) a virus is a plain disc. */
  minSpikyRadiusPx: 5,
});

/** Spike count for a radius: `clamp(round(0.18·r), 12, 60)` (architecture §9.3). */
export function virusSpikeCount(r: number): number {
  const n = Math.round(VIRUS_STYLE.spikesPerRadius * r);
  return Math.min(VIRUS_STYLE.maxSpikes, Math.max(VIRUS_STYLE.minSpikes, n));
}

/** True when a virus of radius `r` shows the "nearly full" look (architecture §9.3). */
export function virusLooksNearlyFull(r: number, info: WelcomeMessage | null): boolean {
  if (info && (info.mode === MODE_CODE.experimental || info.mode === MODE_CODE.battleroyale)) {
    return false;
  }
  const feeds = virusFeedsFromRadius(
    r,
    info?.virusMass ?? SIM_DEFAULTS.virus.mass,
    info?.ejectPelletMass ?? SIM_DEFAULTS.eject.pelletMass,
  );
  return (
    feeds > 0 &&
    isVirusNearlyFull(feeds, info?.virusFeedsToShoot ?? SIM_DEFAULTS.virus.feedsToShoot)
  );
}

/** The ±2 % 6 Hz wobble factor of a nearly-full virus (phase-shifted per id). */
export function virusWobble(id: number, nowMs: number): number {
  const phase = ((id * 0.618034) % 1) * TAU;
  return (
    1 + VIRUS_STYLE.wobbleAmplitude * Math.sin((TAU * VIRUS_STYLE.wobbleHz * nowMs) / 1000 + phase)
  );
}

export interface SpikeArt {
  readonly virus: AssetImage | null;
  readonly virusFed: AssetImage | null;
  readonly spawner: AssetImage | null;
}

/**
 * Adds the spiky outline path: `2n` vertices alternating tip and valley, rotated by `rotation`,
 * optionally jittered (sketch: seeded by `seed` and `frame`).
 */
export function traceSpikes(
  ctx: Frame['ctx'],
  x: number,
  y: number,
  r: number,
  spikes: number,
  rotation: number,
  jitter: number,
  seed: number,
  frame: number,
): void {
  const tip = r * (1 + VIRUS_STYLE.tip);
  const valley = r * (1 - VIRUS_STYLE.valley);
  const count = spikes * 2;
  const step = Math.PI / spikes;
  for (let i = 0; i < count; i++) {
    const a = rotation + i * step;
    let rho = i % 2 === 0 ? tip : valley;
    if (jitter > 0) rho *= 1 + jitter * noise3(seed, i, frame);
    const px = x + rho * Math.cos(a);
    const py = y + rho * Math.sin(a);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/** A drawn body: display position and (eased, effect-applied) radius. */
export interface BodyShape {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

/**
 * A virus (kind 3). Classic: agar's 50-spike two-fill body (`off` = jelly offsets for its 100
 * vertices, or null); sketch: the scribbled spikes with the nearly-full cue. User art wins.
 */
export function drawVirus(
  f: Frame,
  rec: EntityRecord,
  info: WelcomeMessage | null,
  art: SpikeArt,
  shape: BodyShape = rec,
  off: Float64Array | null = null,
): void {
  if (f.theme.style !== 'sketch') {
    const image = art.virus;
    if (image) {
      drawSpikyBody(f, shape, shape.r, f.theme.virus, 0, image);
      return;
    }
    if (!circleInRect(shape.x, shape.y, shape.r + CLASSIC_VIRUS.bandU, f.view)) return;
    f.stats.bodies++;
    drawClassicVirus(f, shape.x, shape.y, shape.r, f.theme.virus, off);
    return;
  }
  const nearlyFull = virusLooksNearlyFull(rec.r, info);
  const fedArt = nearlyFull ? art.virusFed : null;
  const scale = nearlyFull && !fedArt && !f.reduceMotion ? virusWobble(rec.id, f.nowMs) : 1;
  const image = fedArt ?? art.virus;
  let fill = f.theme.virus;
  if (nearlyFull && f.reduceMotion && !fedArt) fill = mixRgb(fill, 0xffffff, 0.3);
  drawSpikyBody(f, shape, shape.r * scale, fill, 0, image);
}

/**
 * The shared spiky body of viruses and spawners: user art (side 2.3 × r, rotated), else the
 * procedural spikes in `fill` with the style's outline. Culls and counts `stats.bodies`.
 */
export function drawSpikyBody(
  f: Frame,
  rec: BodyShape,
  r: number,
  fill: Rgb,
  rotation: number,
  image: AssetImage | null,
): void {
  const { ctx, zoom } = f;
  const x = rec.x;
  const y = rec.y;
  if (!(r > 0)) return;
  if (image) {
    const half = (r * VIRUS_STYLE.artK) / 2;
    if (!circleInRect(x, y, half, f.view)) return;
    f.stats.bodies++;
    if (rotation !== 0) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.drawImage(image, -half, -half, half * 2, half * 2);
      ctx.restore();
    } else {
      ctx.drawImage(image, x - half, y - half, half * 2, half * 2);
    }
    return;
  }
  if (!circleInRect(x, y, r * (1 + VIRUS_STYLE.tip), f.view)) return;
  f.stats.bodies++;
  const rPx = r * zoom;
  const sketch = f.theme.style === 'sketch';
  const lw = (sketch ? sketchStrokePx(rPx) : classicStrokePx(rPx) * 1.15) / zoom;
  ctx.beginPath();
  if (rPx < VIRUS_STYLE.minSpikyRadiusPx) {
    traceCircle(ctx, x, y, r);
  } else {
    const jitter = sketch && !f.quality.perfectCircles ? 0.025 : 0;
    traceSpikes(ctx, x, y, r - lw / 2, virusSpikeCount(r), rotation, jitter, rec.id, f.boilFrame);
  }
  ctx.fillStyle = css(fill);
  ctx.fill();
  ctx.lineWidth = lw;
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 4;
  ctx.strokeStyle = css(sketch ? f.theme.colors.border : classicOutlineColor(fill));
  ctx.stroke();
  if (sketch && rPx >= 12) {
    // The scribbled look's dashed inner ring.
    ctx.beginPath();
    traceCircle(ctx, x, y, r * 0.72);
    ctx.setLineDash([r * 0.12, r * 0.08]);
    ctx.lineWidth = lw * 0.6;
    ctx.strokeStyle = cssAlpha(f.theme.colors.border, 0.6);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}
